import { useCallback, useState, useEffect, useRef } from 'react';
import { notify } from '@/lib/notify';
import type { TelegramMessage, GetMessagesOptions, SendMessageData, SendFileData, ForwardOptions } from '../types';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface UseTelegramMessagesOptions extends Partial<GetMessagesOptions> {
  /** readOutboxMaxId из выбранного чата для определения прочитанности */
  readOutboxMaxId?: number | null;
}

// Генерация локального ID для оптимистичных сообщений (как в Telegram Web A)
let localMessageCounter = -1;
function generateLocalMessageId(): number {
  return localMessageCounter--;
}

// Тип для локального сообщения (пока ждём ответа сервера)
interface LocalMessage extends TelegramMessage {
  isLocal?: boolean;
  sendingState?: 'pending' | 'failed';
  /** Для retry - храним reply id */
  localReplyToMsgId?: number;
}

/**
 * Hook для работы с сообщениями Telegram
 * Реализация точь в точь как в Telegram Web A:
 * - Оптимистичные обновления (локальное сообщение показывается сразу)
 * - Rollback при ошибках
 * - clearDraft после отправки
 */
const MESSAGES_PER_PAGE = 50;

export function useTelegramMessages(chatId: number | null, options: UseTelegramMessagesOptions = {}) {
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isMarkingPublic, setIsMarkingPublic] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  // Храним маппинг localId -> serverMessage для обновления после ответа сервера
  const pendingMessagesRef = useRef<Map<number, LocalMessage>>(new Map());

  // readOutboxMaxId берём из options (передаётся из данных чата)
  const readOutboxMaxId = options.readOutboxMaxId ?? 0;

  // Ref для отслеживания предыдущего chatId (для abort при смене)
  const prevChatIdRef = useRef<number | null>(null);
  // Ref для текущего AbortController
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Отменить серверные запросы для предыдущего чата
   */
  const abortPreviousChat = useCallback((previousChatId: number) => {
    // Отменяем локальные запросы
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Отменяем серверные запросы
    fetch(`${API_URL}/api/telegram-client/chats/${previousChatId}/abort`, {
      method: 'POST',
    }).catch(() => {
      // Игнорируем ошибки abort — не критично
    });
  }, []);

  const fetchMessages = useCallback(async () => {
    if (!chatId) {return;}

    // Запоминаем chatId на момент начала запроса для защиты от race condition
    const requestChatId = chatId;

    // Создаём новый AbortController для этого запроса
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    try {
      const limit = options.limit ?? MESSAGES_PER_PAGE;
      const queryString = new URLSearchParams({
        limit: String(limit),
        ...(options.beforeId && { beforeId: String(options.beforeId) }),
      }).toString();

      const response = await fetch(`${API_URL}/api/telegram-client/chats/${requestChatId}/messages?${queryString}`, {
        signal: controller.signal,
      });

      // Проверяем что chatId не изменился пока ждали ответ
      if (prevChatIdRef.current !== requestChatId) {return;}

      const result: ApiResponse<TelegramMessage[]> = await response.json();
      if (result.success && result.data) {
        // Сохраняем локальные pending сообщения и добавляем к серверным
        const pendingMessages = [...pendingMessagesRef.current.values()];
        setMessages([...result.data, ...pendingMessages]);
        // Если получили меньше чем запрашивали — больше нет сообщений
        setHasMore((result.data?.length ?? 0) >= limit);
      }
    } catch (fetchError) {
      // AbortError — запрос был отменён при смене чата, не ошибка
      if (fetchError instanceof DOMException && fetchError.name === 'AbortError') {
        return;
      }
      // Error fetching messages - will be handled by isLoading state
    } finally {
      // Обновляем loading только если chatId не изменился
      if (prevChatIdRef.current === requestChatId) {
        setIsLoading(false);
      }
    }
  }, [chatId, options.limit, options.beforeId]);

  /**
   * Загрузить более старые сообщения (для infinite scroll)
   */
  const fetchMoreMessages = useCallback(async (): Promise<boolean> => {
    if (!chatId || isLoadingMore || !hasMore) {return false;}

    // Найти самое старое сообщение (с минимальным id) среди НЕ локальных
    const serverMessages = messages.filter(m => !('isLocal' in m && (m as LocalMessage).isLocal));
    if (serverMessages.length === 0) {return false;}

    const oldestMessage = serverMessages.reduce((min, msg) =>
      msg.id < min.id ? msg : min
    , serverMessages[0]);

    setIsLoadingMore(true);
    try {
      const limit = options.limit ?? MESSAGES_PER_PAGE;
      const queryString = new URLSearchParams({
        limit: String(limit),
        beforeId: String(oldestMessage.id),
      }).toString();

      const response = await fetch(`${API_URL}/api/telegram-client/chats/${chatId}/messages?${queryString}`, {
        signal: abortControllerRef.current?.signal,
      });

      const result: ApiResponse<TelegramMessage[]> = await response.json();
      if (result.success && result.data) {
        // Добавляем старые сообщения в начало списка
        setMessages(prev => [...result.data!, ...prev]);
        // Если получили меньше чем запрашивали — больше нет сообщений
        setHasMore((result.data?.length ?? 0) >= limit);
        return (result.data?.length ?? 0) > 0;
      }
      return false;
    } catch (fetchError) {
      // AbortError — запрос был отменён при смене чата
      if (fetchError instanceof DOMException && fetchError.name === 'AbortError') {
        return false;
      }
      return false;
    } finally {
      setIsLoadingMore(false);
    }
  }, [chatId, messages, isLoadingMore, hasMore, options.limit]);

  useEffect(() => {
    // Отменяем запросы предыдущего чата при смене
    const previousChatId = prevChatIdRef.current;
    if (previousChatId !== null && previousChatId !== chatId) {
      abortPreviousChat(previousChatId);
    }
    prevChatIdRef.current = chatId;

    // Немедленно очищаем сообщения при смене чата — убирает "мерцание" старых данных
    setMessages([]);
    setHasMore(true);
    pendingMessagesRef.current.clear();

    if (chatId) {
      fetchMessages();
    }

    // Cleanup: отменяем при unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [chatId, fetchMessages, abortPreviousChat]);

  /**
   * Отправка сообщения с оптимистичным обновлением (как в Telegram Web A)
   * 1. Создаём локальное сообщение и показываем сразу
   * 2. Отправляем на сервер
   * 3. При успехе - заменяем локальное на серверное
   * 4. При ошибке - помечаем как failed или удаляем
   */
  const sendMessage = useCallback(async (text: string, replyToMessageId?: number): Promise<TelegramMessage | null> => {
    if (!chatId) {return null;}

    // 1. Создаём оптимистичное локальное сообщение
    const localId = generateLocalMessageId();
    const localMessage: LocalMessage = {
      id: localId,
      text,
      fromId: 0, // Будет заполнено сервером
      date: new Date(),
      isOutgoing: true,
      isPublic: false,
      replyToMsgId: replyToMessageId ?? null,
      isLocal: true,
      sendingState: 'pending',
      localReplyToMsgId: replyToMessageId,
    };

    // 2. Добавляем в список сообщений сразу (оптимистичное обновление)
    pendingMessagesRef.current.set(localId, localMessage);
    setMessages(prev => [...prev, localMessage]);

    setIsSending(true);
    try {
      const messageData: SendMessageData = { text, replyTo: replyToMessageId };
      const response = await fetch(`${API_URL}/api/telegram-client/chats/${chatId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(messageData),
      });

      const result: ApiResponse<TelegramMessage> = await response.json();

      if (!result.success || !result.data) {
        // 3a. Ошибка - помечаем сообщение как failed
        setMessages(prev => prev.map(msg =>
          msg.id === localId
            ? { ...msg, sendingState: 'failed' }
            : msg
        ));
        notify.error(result.error || 'Ошибка при отправке');
        return null;
      }

      // 3b. Успех - заменяем локальное сообщение на серверное
      pendingMessagesRef.current.delete(localId);
      setMessages(prev => prev.map(msg =>
        msg.id === localId ? result.data! : msg
      ));

      return result.data;
    } catch {
      // Ошибка сети - помечаем как failed
      setMessages(prev => prev.map(msg =>
        msg.id === localId
          ? { ...msg, sendingState: 'failed' }
          : msg
      ));
      notify.error('Ошибка сети');
      return null;
    } finally {
      setIsSending(false);
    }
  }, [chatId]);

  /**
   * Редактирование сообщения с оптимистичным обновлением
   * При ошибке - rollback к исходному тексту
   * Теперь работает с chatId + telegramMessageId (сообщения не хранятся в БД)
   */
  const editMessage = useCallback(async (telegramMessageId: number, text: string): Promise<TelegramMessage | null> => {
    if (!chatId) {return null;}

    // Сохраняем оригинальное сообщение для rollback
    const originalMessage = messages.find(m => m.id === telegramMessageId);
    if (!originalMessage) {return null;}

    // Оптимистичное обновление
    setMessages(prev => prev.map(msg =>
      msg.id === telegramMessageId ? { ...msg, text } : msg
    ));

    setIsEditing(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/chats/${chatId}/messages/${telegramMessageId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text }),
      });

      const result: ApiResponse<null> = await response.json();

      if (!result.success) {
        // Rollback при ошибке
        setMessages(prev => prev.map(msg =>
          msg.id === telegramMessageId ? originalMessage : msg
        ));
        notify.error(result.error || 'Ошибка при редактировании');
        return null;
      }

      notify.success('Сообщение отредактировано');
      // Возвращаем обновлённое сообщение
      return { ...originalMessage, text };
    } catch {
      // Rollback при ошибке сети
      setMessages(prev => prev.map(msg =>
        msg.id === telegramMessageId ? originalMessage : msg
      ));
      notify.error('Ошибка сети');
      return null;
    } finally {
      setIsEditing(false);
    }
  }, [chatId, messages]);

  /**
   * Удаление сообщения с оптимистичным обновлением
   * Теперь работает с chatId + telegramMessageId (сообщения не хранятся в БД)
   */
  const deleteMessage = useCallback(async (telegramMessageId: number, shouldDeleteForAll = true): Promise<boolean> => {
    if (!chatId) {return false;}

    // Сохраняем для rollback
    const originalMessage = messages.find(m => m.id === telegramMessageId);

    // Оптимистичное удаление
    setMessages(prev => prev.filter(msg => msg.id !== telegramMessageId));

    setIsDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/chats/${chatId}/messages/${telegramMessageId}?shouldDeleteForAll=${shouldDeleteForAll}`, {
        method: 'DELETE',
      });

      const result: ApiResponse<null> = await response.json();

      if (!result.success) {
        // Rollback - возвращаем сообщение
        if (originalMessage) {
          setMessages(prev => [...prev, originalMessage].sort((a, b) => a.id - b.id));
        }
        notify.error(result.error || 'Ошибка при удалении');
        return false;
      }

      notify.success('Сообщение удалено');
      return true;
    } catch {
      // Rollback при ошибке сети
      if (originalMessage) {
        setMessages(prev => [...prev, originalMessage].sort((a, b) => a.id - b.id));
      }
      notify.error('Ошибка сети');
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [chatId, messages]);

  /**
   * Пересылка сообщения
   * Теперь работает с chatId + telegramMessageId
   */
  const forwardMessage = useCallback(async (
    telegramMessageId: number,
    toChatId: number,
    options?: ForwardOptions
  ): Promise<boolean> => {
    if (!chatId) {return false;}

    try {
      const response = await fetch(`${API_URL}/api/telegram-client/chats/${chatId}/messages/${telegramMessageId}/forward`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          toChatId,
          noAuthors: options?.noAuthors,
          noCaptions: options?.noCaptions,
          silent: options?.silent,
        }),
      });

      const result: ApiResponse<TelegramMessage[]> = await response.json();

      if (!result.success) {
        notify.error(result.error || 'Ошибка при пересылке');
        return false;
      }

      notify.success('Сообщение переслано');
      return true;
    } catch {
      notify.error('Ошибка сети');
      return false;
    }
  }, [chatId]);

  /**
   * Пересылка нескольких сообщений
   * Теперь работает с chatId + telegramMessageIds
   */
  const forwardMessages = useCallback(async (
    telegramMessageIds: number[],
    toChatId: number,
    options?: ForwardOptions
  ): Promise<boolean> => {
    if (!chatId) {return false;}

    try {
      const response = await fetch(`${API_URL}/api/telegram-client/chats/${chatId}/messages/forward`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          telegramMessageIds,
          toChatId,
          noAuthors: options?.noAuthors,
          noCaptions: options?.noCaptions,
          silent: options?.silent,
        }),
      });

      const result: ApiResponse<TelegramMessage[]> = await response.json();

      if (!result.success) {
        notify.error(result.error || 'Ошибка при пересылке');
        return false;
      }

      notify.success(`Переслано ${telegramMessageIds.length} сообщений`);
      return true;
    } catch {
      notify.error('Ошибка сети');
      return false;
    }
  }, [chatId]);

  /**
   * Пометить сообщения как публичные
   * Теперь работает с chatId + telegramMessageIds
   * При пометке публичными — сохраняет в БД, при снятии — удаляет из БД
   * @param telegramMessageIds - ID сообщений для публикации
   * @param orderId - опциональный ID заказа для привязки чата
   */
  const markAsPublic = useCallback(async (telegramMessageIds: number[], orderId?: number, isPublic = true): Promise<TelegramMessage[]> => {
    if (!chatId) {return [];}

    setIsMarkingPublic(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/chats/${chatId}/messages/mark-public`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ telegramMessageIds, isPublic, orderId }),
      });

      const result: ApiResponse<TelegramMessage[]> = await response.json();

      if (!result.success) {
        notify.error(result.error || 'Ошибка при изменении публичности');
        return [];
      }

      // Обновляем локально
      setMessages(prev => prev.map(msg =>
        telegramMessageIds.includes(msg.id) ? { ...msg, isPublic } : msg
      ));

      if (isPublic) {
        notify.success(orderId
          ? 'Сообщения опубликованы и чат привязан к заказу'
          : 'Сообщения помечены как публичные'
        );
      } else {
        notify.success('Публичность снята');
      }
      return result.data || [];
    } catch {
      notify.error('Ошибка сети');
      return [];
    } finally {
      setIsMarkingPublic(false);
    }
  }, [chatId]);

  /**
   * Отправка файла с оптимистичным обновлением
   */
  const sendFile = useCallback(async (fileData: SendFileData): Promise<TelegramMessage | null> => {
    if (!chatId) {return null;}

    // Создаём оптимистичное сообщение для файла
    const localId = generateLocalMessageId();
    const localMessage: LocalMessage = {
      id: localId,
      text: fileData.caption || null,
      fromId: 0,
      date: new Date(),
      isOutgoing: true,
      isPublic: false,
      media: { type: 'document' }, // Будет уточнено сервером
      replyToMsgId: fileData.replyTo ?? null,
      isLocal: true,
      sendingState: 'pending',
      localReplyToMsgId: fileData.replyTo,
    };

    pendingMessagesRef.current.set(localId, localMessage);
    setMessages(prev => [...prev, localMessage]);

    setIsUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', fileData.file);
      if (fileData.caption) {
        formData.append('caption', fileData.caption);
      }
      if (fileData.replyTo) {
        formData.append('replyTo', String(fileData.replyTo));
      }

      const response = await fetch(`${API_URL}/api/telegram-client/chats/${chatId}/files`, {
        method: 'POST',
        body: formData,
      });

      const result: ApiResponse<TelegramMessage> = await response.json();

      if (!result.success || !result.data) {
        setMessages(prev => prev.map(msg =>
          msg.id === localId ? { ...msg, sendingState: 'failed' } : msg
        ));
        notify.error(result.error || 'Ошибка при отправке файла');
        return null;
      }

      // Заменяем локальное на серверное
      pendingMessagesRef.current.delete(localId);
      setMessages(prev => prev.map(msg =>
        msg.id === localId ? result.data! : msg
      ));

      notify.success('Файл отправлен');
      return result.data;
    } catch {
      setMessages(prev => prev.map(msg =>
        msg.id === localId ? { ...msg, sendingState: 'failed' } : msg
      ));
      notify.error('Ошибка сети');
      return null;
    } finally {
      setIsUploadingFile(false);
    }
  }, [chatId]);

  const sendSticker = useCallback(async (stickerId: string, replyTo?: number): Promise<TelegramMessage | null> => {
    if (!chatId) {return null;}

    // Оптимистичное сообщение для стикера
    const localId = generateLocalMessageId();
    const localMessage: LocalMessage = {
      id: localId,
      text: null,
      fromId: 0,
      date: new Date(),
      isOutgoing: true,
      isPublic: false,
      media: { type: 'sticker' },
      replyToMsgId: replyTo ?? null,
      isLocal: true,
      sendingState: 'pending',
      localReplyToMsgId: replyTo,
    };

    pendingMessagesRef.current.set(localId, localMessage);
    setMessages(prev => [...prev, localMessage]);

    setIsSending(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/chats/${chatId}/stickers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ stickerId, replyTo }),
      });

      const result: ApiResponse<TelegramMessage> = await response.json();

      if (!result.success || !result.data) {
        setMessages(prev => prev.map(msg =>
          msg.id === localId ? { ...msg, sendingState: 'failed' } : msg
        ));
        notify.error(result.error || 'Ошибка при отправке стикера');
        return null;
      }

      pendingMessagesRef.current.delete(localId);
      setMessages(prev => prev.map(msg =>
        msg.id === localId ? result.data! : msg
      ));

      return result.data;
    } catch {
      setMessages(prev => prev.map(msg =>
        msg.id === localId ? { ...msg, sendingState: 'failed' } : msg
      ));
      notify.error('Ошибка сети');
      return null;
    } finally {
      setIsSending(false);
    }
  }, [chatId]);

  /**
   * Отправка индикатора набора текста
   */
  const sendTypingAction = useCallback(async (action: 'typing' | 'cancel' = 'typing'): Promise<void> => {
    if (!chatId) {return;}

    try {
      await fetch(`${API_URL}/api/telegram-client/chats/${chatId}/typing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      });
    } catch {
      // Игнорируем ошибки typing
    }
  }, [chatId]);

  /**
   * Повторная отправка failed сообщения
   */
  const retrySendMessage = useCallback(async (localId: number): Promise<boolean> => {
    const failedMessage = messages.find(m => m.id === localId && (m as LocalMessage).sendingState === 'failed') as LocalMessage | undefined;
    if (!failedMessage || !failedMessage.text) {return false;}

    // Удаляем failed сообщение
    setMessages(prev => prev.filter(m => m.id !== localId));
    pendingMessagesRef.current.delete(localId);

    // Пробуем отправить снова, используя localReplyToMsgId (сохраняется при создании)
    const result = await sendMessage(failedMessage.text, failedMessage.localReplyToMsgId ?? failedMessage.replyToMsgId ?? undefined);
    return result !== null;
  }, [messages, sendMessage]);

  /**
   * Удаление локального failed сообщения
   */
  const removeFailedMessage = useCallback((localId: number): void => {
    setMessages(prev => prev.filter(m => m.id !== localId));
    pendingMessagesRef.current.delete(localId);
  }, []);

  // Проверка прочитанности сообщения (как в Telegram)
  // Исходящее сообщение считается прочитанным, если его ID <= readOutboxMaxId
  const isMessageRead = useCallback((message: TelegramMessage): boolean => {
    if (!message.isOutgoing) {return true;} // Входящие всегда "прочитаны" с нашей стороны
    // Локальные сообщения ещё не прочитаны
    if ('isLocal' in message && (message as LocalMessage).isLocal) {return false;}
    // Теперь id — это telegramMessageId
    return message.id <= readOutboxMaxId;
  }, [readOutboxMaxId]);

  /**
   * Пометить сообщения в чате как прочитанные
   * Вызывается когда пользователь открывает чат или прокручивает к новым сообщениям
   */
  const markAsRead = useCallback(async (maxId?: number): Promise<void> => {
    if (!chatId) {return;}

    try {
      await fetch(`${API_URL}/api/telegram-client/chats/${chatId}/read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ maxId }),
      });
    } catch {
      // Игнорируем ошибки mark as read — не критично
    }
  }, [chatId]);

  /**
   * Обновить сообщение в списке (при получении события редактирования через WS)
   */
  const updateMessage = useCallback((messageId: number, updatedMessage: TelegramMessage): void => {
    setMessages(prev => prev.map(msg =>
      msg.id === messageId ? { ...msg, ...updatedMessage } : msg
    ));
  }, []);

  /**
   * Удалить сообщения из списка (при получении события удаления через WS)
   */
  const removeMessages = useCallback((messageIds: number[]): void => {
    setMessages(prev => prev.filter(msg => !messageIds.includes(msg.id)));
  }, []);

  return {
    messages,
    isLoading,
    isLoadingMore,
    hasMore,
    isSending,
    isDeleting,
    isEditing,
    isMarkingPublic,
    isUploadingFile,
    refetch: fetchMessages,
    fetchMoreMessages,
    sendMessage,
    editMessage,
    deleteMessage,
    forwardMessage,
    forwardMessages,
    markAsPublic,
    sendFile,
    sendSticker,
    sendTypingAction,
    retrySendMessage,
    removeFailedMessage,
    isMessageRead,
    markAsRead,
    updateMessage,
    removeMessages,
  };
}
